import { RefObject, useLayoutEffect } from 'react';
import { computeFitHeight } from '../utils/computeFitHeight';

// Header plus about five rows. Below this the page scrolls rather than the table shrinking further.
const minFitViewportHeight = 280;

const getBottomInset = (element: HTMLElement) => {
  const style = window.getComputedStyle(element);
  return (
    (parseFloat(style.paddingBottom) || 0) +
    (parseFloat(style.borderBottomWidth) || 0) +
    (parseFloat(style.marginBottom) || 0)
  );
};

/**
 * Sizes `wrapperRef` so that `sectionRef` ends at the bottom of the area the page scrolls in:
 * the nearest scrolling ancestor, or the viewport. Works when the parent has no fixed height,
 * and lets the wrapper shrink to the table when the table is shorter.
 */
export const useFitViewportHeight = (
  sectionRef: RefObject<HTMLElement | null>,
  wrapperRef: RefObject<HTMLElement | null>,
  enabled: boolean
) => {
  useLayoutEffect(() => {
    const section = sectionRef.current;
    const wrapper = wrapperRef.current;
    const table = wrapper?.querySelector('table');
    if (!enabled || !section || !wrapper || !table) return;

    // The space below the table is measured from the section and the ancestors' own padding,
    // border and margin, not from the page height, which grows with the table and would never
    // let it shrink.
    const ancestors: HTMLElement[] = [];
    let scrollRoot: HTMLElement | null = null;
    for (
      let ancestor = section.parentElement;
      ancestor && ancestor !== document.documentElement;
      ancestor = ancestor.parentElement
    ) {
      const { overflowY } = window.getComputedStyle(ancestor);
      if (ancestor !== document.body && (overflowY === 'auto' || overflowY === 'scroll')) {
        scrollRoot = ancestor;
        break;
      }
      ancestors.push(ancestor);
    }

    const recalc = () => {
      const wrapperRect = wrapper.getBoundingClientRect();
      const belowWrapper =
        section.getBoundingClientRect().bottom -
        wrapperRect.bottom +
        (parseFloat(window.getComputedStyle(section).marginBottom) || 0);
      const top = scrollRoot
        ? wrapperRect.top - scrollRoot.getBoundingClientRect().top + scrollRoot.scrollTop
        : wrapperRect.top + window.scrollY;
      // clientHeight leaves out scrollbars and, unlike visualViewport, does not shrink on pinch-zoom.
      const viewportHeight = (scrollRoot ?? document.documentElement).clientHeight;

      const heightPx = `${computeFitHeight({
        viewportHeight,
        top,
        bottomInset: ancestors.reduce(
          (sum, ancestor) => sum + getBottomInset(ancestor),
          belowWrapper
        ),
        // The wrapper's border and horizontal scrollbar sit outside the table, so a short table
        // would otherwise get a few pixels of inner scroll.
        contentHeight: table.offsetHeight + wrapper.offsetHeight - wrapper.clientHeight,
        minHeight: minFitViewportHeight,
      })}px`;

      if (wrapper.style.height === heightPx) return;
      wrapper.style.height = heightPx;
      wrapper.style.maxHeight = heightPx;
    };

    recalc();

    // The table catches new rows and filtering, the section catches a taller header, and the
    // ancestors catch content above it changing, such as an accordion or the sidebar.
    const observer = new ResizeObserver(recalc);
    [table, section, ...ancestors].forEach((element) => observer.observe(element));
    window.addEventListener('resize', recalc);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', recalc);
      wrapper.style.height = '';
      wrapper.style.maxHeight = '';
    };
  }, [enabled, sectionRef, wrapperRef]);
};
